import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertNumberToWords } from "./utility";

export const generateRetourFournisseur = (
    retour,
    {
        company = {},
        showPrices = true,
        priceMode = "TTC", // "HT" or "TTC"
        format = "A4",     // "A4" or "A5"
    } = {}
) => {

    const doc = new jsPDF({ unit: "mm", format: format.toLowerCase() });

    /* ================== PAGE CONSTANTS ================== */
    const PAGE_W = doc.internal.pageSize.width;
    const PAGE_H = doc.internal.pageSize.height;

    // proportional margins (slightly smaller on A5 automatically)
    const M = format === "A4" ? 20 : 12;
    const FOOTER_H = format === "A4" ? 22 : 18;

    /* ================== BRAND STYLE ================== */
    const BRAND = {
        primary: [6, 145, 43],
        ink: [15, 23, 42],
        subtle: [100, 116, 139],
        line: [226, 232, 240],
        headerBg: [241, 245, 249],
        totalBg: [163, 240, 183],
    };

    const fmtMoney = (v) =>
        Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const frDate = (d) =>
        d ? new Date(d).toLocaleDateString("fr-FR") : "-";

    const amountWords = (n) =>
        convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DH";

    /* ================== HEADER (same style both formats) ================== */
    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 22 : 18);
        doc.setTextColor(...BRAND.primary);

        doc.text(company.name || "MA SOCIÉTÉ", PAGE_W / 2, M, { align: "center" });

        if (company.subtitle) {
            doc.setFontSize(format === "A4" ? 10 : 9);
            doc.setTextColor(...BRAND.subtle);
            doc.text(company.subtitle, PAGE_W / 2, M + 7, { align: "center" });
        }

        doc.setDrawColor(...BRAND.line);
        doc.line(M, M + 12, PAGE_W - M, M + 12);
    };

    /* ================== INFO CARDS (scales automatically) ================== */
    const CARD_W = (PAGE_W - M * 3) / 2;
    const CARD_H = format === "A4" ? 45 : 35;
    const CARD_Y = M + 16;

    const drawCard = (x, title, lines) => {
        doc.setDrawColor(0);
        doc.roundedRect(x, CARD_Y, CARD_W, CARD_H, 3, 3);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 11 : 9);
        doc.setTextColor(...BRAND.ink);
        doc.text(title, x + 5, CARD_Y + 7);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(format === "A4" ? 9 : 8);
        doc.setTextColor(...BRAND.subtle);

        (lines || []).forEach((l, i) => {
            if (!l) return;
            doc.text(String(l), x + 5, CARD_Y + 14 + i * (format === "A4" ? 6 : 5));
        });
    };

    /* ================== TABLE ================== */
    const tableY = CARD_Y + CARD_H + (format === "A4" ? 10 : 6);

    const headers = showPrices
        ? ["#", "Article", "Qté", "UM", priceMode === "HT" ? "PU HT" : "PU TTC", priceMode === "HT" ? "Total HT" : "Total TTC"]
        : ["#", "Article", "Qté", "UM"];

    const rows = (retour.lignes || []).map((l, i) => {
        const qt = l.quantiteRetournee;
        const pu = priceMode === "HT" ? l.prixUnitaireHT : l.prixUnitaireTTC;
        const tot = priceMode === "HT" ? qt * l.prixUnitaireHT : qt * l.prixUnitaireTTC;

        return showPrices
            ? [i + 1, l.articleNom, qt, l.unite, fmtMoney(pu), fmtMoney(tot)]
            : [i + 1, l.articleNom, qt, l.unite];
    });

    drawHeader();

    drawCard(M, "RETOUR FOURNISSEUR", [
        `N° : ${retour.numeroRetour || "-"}`,
        `Date : ${frDate(retour.dateRetour)}`,
        retour.transporteurName ? `Transporteur : ${retour.transporteurName}` : null
    ]);

    drawCard(
        M * 2 + CARD_W,
        "FOURNISSEUR",
        [
            retour.fournisseurNom,
            retour.fournisseurVille,
            `Tél : ${retour.fournisseurTelephone || "-"}`,
        ]
    );

    autoTable(doc, {
        startY: tableY,
        head: [headers],
        body: rows,
        styles: { fontSize: format === "A4" ? 9 : 7, cellPadding: format === "A4" ? 3 : 2 },
        headStyles: {
            fillColor: BRAND.headerBg,
            textColor: BRAND.ink,
            fontStyle: "bold",
        },
        tableLineColor: BRAND.line,
        tableLineWidth: 0.2,
    });

    /* ================== TOTALS BLOCK (same look both formats) ================== */
    const drawTotals = (y) => {
        const boxW = format === "A4" ? 70 : 60;
        const x = PAGE_W - M - boxW;

        doc.setFillColor(...BRAND.totalBg);
        doc.roundedRect(x, y, boxW, format === "A4" ? 24 : 18, 3, 3, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(format === "A4" ? 11 : 9);
        doc.setTextColor(...BRAND.ink);

        doc.text("TOTAL TTC", x + 5, y + 9);

        doc.text(fmtMoney(retour.montantTTC), x + boxW - 5, y + (format === "A4" ? 18 : 13), {
            align: "right",
        });
    };

    const yAfter = doc.lastAutoTable.finalY + (format === "A4" ? 10 : 6);

    if (showPrices) {
        drawTotals(yAfter);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(format === "A4" ? 9 : 8);
        doc.setTextColor(...BRAND.subtle);
        doc.text(
            `Arrêté à la somme de : ${amountWords(retour.montantTTC)}`,
            M,
            yAfter + (format === "A4" ? 32 : 24)
        );
    }

    /* ================== FOOTER ================== */
    doc.setDrawColor(0);
    doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);

    doc.setFontSize(format === "A4" ? 8 : 7);
    doc.setTextColor(80);

    doc.text(
        `${company.address || ""} • ${company.phone1 || ""} • ${company.email || ""}`,
        PAGE_W / 2,
        PAGE_H - 8,
        { align: "center" }
    );

    return doc;
};
